import { Menu } from '@zillow/constellation-10';
import {
  IconArchiveOutline,
  IconConcealOutline,
  IconRestoreMailOutline,
  IconSpamFolderOutline,
} from '@zillow/constellation-icons';
import type { Actions, ConversationStatus } from '../';

export type MenuContentProps = {
  actions: Actions;
  additionalMenuItem?: React.ReactNode;
  isBlocked: boolean;
  isImpersonator: boolean;
  moveToInboxLabel?: 'Move to inbox' | 'Move to recent';
  shouldHideAdditionalMenuItemsOnDesktop?: boolean;
  shouldShowMarkUnread?: boolean;
  shouldShowMoveToInbox: boolean;
  status: ConversationStatus | null;
};

export default function MenuContent({
  actions,
  additionalMenuItem,
  isBlocked,
  isImpersonator,
  moveToInboxLabel = 'Move to inbox',
  shouldHideAdditionalMenuItemsOnDesktop = false,
  shouldShowMarkUnread = false,
  shouldShowMoveToInbox,
  status,
}: MenuContentProps) {
  const {
    archiveConversation,
    markConversationAsUnread,
    openReportConversationModal,
    undoArchiveConversation,
    undoReportConversation,
  } = actions;

  const { isArchived, isSpam } = status || {};
  const shouldShowSimplifiedMenu = additionalMenuItem && (isArchived || isSpam);

  const renderAdditionalMenuItem = () =>
    additionalMenuItem && (
      <Menu.Group css={{ ...(shouldHideAdditionalMenuItemsOnDesktop && { xl: { display: 'none' } }) }}>
        <Menu.Item asChild>{additionalMenuItem}</Menu.Item>
      </Menu.Group>
    );

  if (shouldShowSimplifiedMenu) {
    return (
      <>
        {renderAdditionalMenuItem()}

        {shouldShowMoveToInbox && (
          <Menu.Group aria-label="Conversation actions">
            <Menu.Item
              disabled={isImpersonator}
              onClick={isArchived ? () => undoArchiveConversation() : () => undoReportConversation()}
            >
              <Menu.ItemIcon render={<IconRestoreMailOutline />} />
              <Menu.ItemLabel>{moveToInboxLabel}</Menu.ItemLabel>
            </Menu.Item>
          </Menu.Group>
        )}
      </>
    );
  }

  return (
    <>
      {renderAdditionalMenuItem()}

      {shouldShowMarkUnread && markConversationAsUnread && (
        <Menu.Group>
          <Menu.Item disabled={isImpersonator} onClick={() => markConversationAsUnread()}>
            <Menu.ItemIcon render={<IconConcealOutline />} />
            <Menu.ItemLabel>Mark unread</Menu.ItemLabel>
          </Menu.Item>
        </Menu.Group>
      )}

      <Menu.Group aria-label="Conversation actions">
        <Menu.Item disabled={isImpersonator} onClick={() => archiveConversation()}>
          <Menu.ItemIcon render={<IconArchiveOutline />} />
          <Menu.ItemLabel>Archive</Menu.ItemLabel>
        </Menu.Item>

        <Menu.Item disabled={isImpersonator || isBlocked} onClick={openReportConversationModal}>
          <Menu.ItemIcon render={<IconSpamFolderOutline />} />
          <Menu.ItemLabel>Report</Menu.ItemLabel>
        </Menu.Item>
      </Menu.Group>
    </>
  );
}
